/**
 * IngestPage.tsx — Ingest / Workspace surface orchestrator (B2-step-3).
 * Holds the view state (dashboard | upload). The upload flow creates a real
 * project, uploads its documents, then asks the user to confirm them and press
 * Avvia (`P-31`, 2026-08-19 — the analysis no longer auto-starts). From that
 * point UploadView is a live progress surface; "back" returns to the dashboard
 * while the server-side run continues (the app-wide watcher announces
 * completion).
 *
 * `P-63`, 2026-09-02 — this page now answers on TWO routes.
 *
 *   `/`                            the workspace, and a brand-new project
 *   `/projects/:projectId/prepare` an EXISTING project's preparation, reopened
 *
 * The second is the whole point of `P-63`. Before it, the wizard was
 * `view === "upload"` and nothing else: a value in one component's memory, with
 * no id in the URL. Navigating away discarded the only route back to a project
 * that already existed on the server with its documents, its price lists and
 * its Knowledge Base proposals intact. Nothing was ever lost except the way
 * back to it — which is why this is a routing fix and not a persistence one.
 *
 * The route param is the authority when present. `view` is deliberately NOT
 * consulted on that route: a remount from a URL must show the project in the
 * URL, never whatever this component last remembered.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiErrorMessage } from "../../lib/api";
import { useLatestAnalysisJob, useProject, useProjectDocuments } from "../../lib/tenderApi";
import { Dashboard } from "./Dashboard";
import { UploadView } from "./UploadView";

type View = "dashboard" | "upload";

/**
 * `P-63` — resolve a reopened project, and render NOTHING of the wizard until it lands.
 *
 * The gate is the load-bearing part, not the fetch. `UploadView` derives its
 * phase as `analysisDone || analysisFailed ? 3 : classified || jobId ||
 * uploadCommitted ? 2 : 1`, and `classified` needs a project. So for the whole
 * hydration window a reopened URL evaluates to **phase 1** — the Fase 1 drop
 * zone, with a project-name field and a commit button whose handler creates a
 * project when `project` is falsy. A user who reopened «Torino Phase 3G», saw
 * an empty upload form and re-dropped the ZIP would have got a SECOND project
 * importing a second copy of the same bundle. That is the same failure `P-48`
 * round 4 recorded for the org-wide import banner, arriving by a new door.
 *
 * It is also simply the honest thing to draw: until the project loads we do not
 * know what it is, and a drop zone is a claim that it is new.
 *
 * A separate component so the two `useQuery` calls are not hooks on the `/`
 * path, where there is no id to fetch and `enabled: false` would still demand a
 * QueryClient from every harness that renders the workspace.
 */
function ReopenedProject({ projectId }: { projectId: string }): JSX.Element {
  const navigate = useNavigate();
  const projectQuery = useProject(projectId);
  const project = projectQuery.data ?? null;
  /* Rediscovers a run started before this reload. `/jobs/latest` defaults to
     `kind=analyze`, so an archive import cannot be mistaken for one. A 404
     (never analysed) comes back as `null`, not as an error.

     Keyed on the RESOLVED project rather than on the raw param: a foreign or
     non-existent id 404s on the project, and firing a second request for its
     jobs afterwards asks about a project this user has just been told does not
     exist. Nothing leaks either way — the route is RLS-scoped — but the request
     has no reason to exist. */
  const latestJob = useLatestAnalysisJob(project?.id ?? null);
  /* The document list is the OTHER half of the phase derivation, and waiting
     for the project alone is not enough. Found by the independent designer
     before this shipped: `phase` needs `classified`, which is `!!project &&
     documents.length > 0`, so between the project landing and the documents
     landing a reopened project evaluates to phase 1 and paints the Fase 1 drop
     zone over a bundle of thirty files. Fetched here purely to know WHEN it has
     arrived; `UploadView` reads the same query key, so react-query serves it
     from cache and this costs no second request. */
  const documentsQuery = useProjectDocuments(project?.id ?? null);
  const back = (): void => {
    void navigate("/");
  };

  if (project && documentsQuery.isFetched && latestJob.isFetched) {
    return (
      <UploadView
        /* `key` — a param-only navigation (the sidebar switcher going straight
           from one project's preparation to another's) reuses this component
           instance, and `UploadView` holds `staged`, `name`, `step`, `viewTab`,
           `startedJobId` and more in plain `useState`, none of which reset on a
           prop change. Without this, project B's screen can show project A's
           job id and, worse, still hold A's staged files against a «Carica
           documenti» click. Predicted by the independent designer from the code
           alone. */
        key={project.id}
        onBack={back}
        reopenedProject={project}
        reopenedJobId={latestJob.data?.id ?? null}
      />
    );
  }

  return (
    <div style={{ padding: "28px 36px 60px", maxWidth: 760, margin: "0 auto" }}>
      <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 16 }}>
        ← Torna al workspace
      </button>
      <div className="card" style={{ padding: 18 }}>
        {projectQuery.isError ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--c-err)" }}>
              Non riusciamo ad aprire questo progetto
            </div>
            <div style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 5 }}>
              {apiErrorMessage(projectQuery.error)}
            </div>
            {/* Answers the question a person actually has here, which is not
                "what went wrong" but "are my documents still there". They are:
                this screen failed to READ the project and has written nothing. */}
            <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 8 }}>
              Nessun documento è stato toccato. Torna al workspace e riprova.
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: "var(--c-ink-3)" }}>Caricamento del progetto…</div>
        )}
      </div>
    </div>
  );
}

export function IngestPage(): JSX.Element {
  const { projectId } = useParams<{ projectId?: string }>();
  const [view, setView] = useState<View>("dashboard");

  if (projectId) return <ReopenedProject projectId={projectId} />;
  if (view === "upload") {
    return <UploadView onBack={() => setView("dashboard")} />;
  }
  return <Dashboard onNew={() => setView("upload")} />;
}
